import React, { useState, useEffect, useMemo, useContext } from "react";
import { Info } from "lucide-react";

// Shared components
import InputDropdown from "../../../Shared/input-dropdown/inputDropdown";
import { StyledButton, buttonStyles } from "../../../Shared/button/button";
import Toast from "../../../Shared/toast/toast";
import PortalOverlay from "../../../Shared/portal-overlay/portalOverlay";
import { callApi } from "../../../api/fetch";
import { environmentContext } from "../../../context/environmentContext";
import { testIdProps } from "../../../utils/testIdProps";
import {
  CUSTOMER_CREATE_PAYMENT_PATH,
  buildCustomerCreatePaymentPayload,
  paymentLinkFromCreateResponse,
} from "../../../utils/paymentGateway";

// Styles
import styles from "./dialog.module.scss";

interface Wallet {
  wallets_id: string;
  wallets_type: string;
  wallet_balance: number;
  environment: string;
}

interface AddBalanceDialogProps {
  show: boolean;
  onClose: () => void;
  walletDetails?: Wallet[];
  onAddBalance: (walletType: string, amount: number) => void;
}

const AddBalanceDialog: React.FC<AddBalanceDialogProps> = ({
  show,
  onClose,
  walletDetails = [],
  onAddBalance,
}) => {
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [amountError, setAmountError] = useState<string>("");

  // Get environment from context
  const environmentCtx = useContext(environmentContext);
  const { environment } = environmentCtx;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedWallet(null);
      setAmount("");
      setAmountError("");
    }
  }, [show]);

  // Create option arrays for dropdown display
  const walletOptions = useMemo(() => {
    // Define the preferred order
    const walletOrder = ["primary", "estamp", "challan"];

    // Sort wallets according to the preferred order
    const sortedWallets = walletDetails.sort((a, b) => {
      const indexA = walletOrder.indexOf(a.wallets_type);
      const indexB = walletOrder.indexOf(b.wallets_type);
      return indexA - indexB;
    });

    return sortedWallets.map((wallet) => {
      return wallet.wallets_type === "primary"
        ? "Primary Wallet"
        : wallet.wallets_type === "estamp"
          ? "E-stamp Wallet"
          : wallet.wallets_type === "challan"
            ? "RC Challan Wallet"
            : wallet.wallets_type || "Unknown Wallet";
    });
  }, [walletDetails]);

  const handleAddBalance = async () => {
    if (!selectedWallet || !amount) return;

    setIsLoading(true);

    try {
      const payload = buildCustomerCreatePaymentPayload({
        amount: parseFloat(amount),
        walletId: selectedWallet.wallets_id,
        walletType: selectedWallet.wallets_type,
        env: environment,
      });

      const response = await callApi(CUSTOMER_CREATE_PAYMENT_PATH, {
        method: "POST",
        data: payload,
      });

      if (response?.err) {
        throw new Error(response.err);
      }

      // Show success message and redirect to payment link
      const walletDisplayName =
        selectedWallet.wallets_type === "primary"
          ? "Primary Wallet"
          : selectedWallet.wallets_type === "estamp"
            ? "E-stamp Wallet"
            : selectedWallet.wallets_type === "challan"
              ? "RC Challan Wallet"
              : selectedWallet.wallets_type;

      Toast(
        "success",
        `Redirecting to payment for ${walletDisplayName}`,
        "Success"
      );

      const linkUrl = paymentLinkFromCreateResponse(response);
      if (linkUrl) {
        window.open(linkUrl, "_blank");
      } else {
        Toast("error", "Payment link not generated", "Error");
        return;
      }

      // parent callback will handle closing dialog and refreshing data
      onAddBalance(selectedWallet.wallets_type, parseFloat(amount));
    } catch (error: any) {
      console.error("Add Balance API Error:", error);
      Toast(
        "error",
        error?.message || "Failed to create payment link",
        "Error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);

      // Validate amount - show error if user enters 0
      if (value && parseFloat(value) === 0) {
        setAmountError("Amount cannot be 0");
        return;
      } else {
        setAmountError("");
      }
    }
  };

  if (!show) return null;

  return (
    <PortalOverlay>
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom"
          style={{
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <span className="mb-0 fs-6 fw-semibold text-dark">
            {`Add Balance - ${environment === "production" ? "Production" : "Sandbox"}`}
          </span>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Wallet Type Dropdown */}
          <div className="mb-4">
            <label
              className="fw-normal form-label text-muted mb-2"
              style={{ fontSize: "14px" }}
            >
              Wallet Type
            </label>
            <InputDropdown
              id="new-billing-wallet-type"
              default_value={
                selectedWallet
                  ? selectedWallet.wallets_type === "primary"
                    ? "Primary Wallet"
                    : selectedWallet.wallets_type === "estamp"
                      ? "E-stamp Wallet"
                      : selectedWallet.wallets_type === "challan"
                        ? "RC Challan Wallet"
                        : selectedWallet.wallets_type
                  : ""
              }
              optionsArray={walletOptions}
              click={(selectedDisplayName: string) => {
                // Find the actual wallet object based on the selected display name
                const walletType =
                  selectedDisplayName === "Primary Wallet"
                    ? "primary"
                    : selectedDisplayName === "E-stamp Wallet"
                      ? "estamp"
                      : selectedDisplayName === "RC Challan Wallet"
                        ? "challan"
                        : "";

                const foundWallet = walletDetails.find(
                  (wallet: Wallet) => wallet.wallets_type === walletType
                );

                setSelectedWallet(foundWallet || null);
              }}
              placeholder="Select Wallet"
            />
          </div>

          {/* Add Amount Input */}
          <div className="mb-4">
            <div className="d-flex justify-content-between">
              <label
                className="fw-normal form-label text-muted mb-2"
                style={{ fontSize: "14px" }}
              >
                Add Amount
              </label>
              <Info size={16} className="text-muted" />
            </div>
            <div className="position-relative">
              <div
                className="position-absolute d-flex align-items-center justify-content-center text-muted"
                style={{
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "14px",
                  zIndex: 1,
                }}
              >
                ₹
              </div>
              <input
                {...testIdProps("new-billing-add-amount")}
                type="text"
                className="form-control bg-light border-0"
                placeholder="e.g. 10,000"
                value={amount}
                onChange={handleAmountChange}
                style={{
                  paddingLeft: "35px",
                  fontSize: "14px",
                  height: "48px",
                }}
              />
            </div>
            {amountError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {amountError}
              </div>
            )}
          </div>

          {/* Note */}
          <div
            className="d-flex align-items-center gap-2 mb-4 rounded-0"
            style={{
              backgroundColor: "#F5F5F5",
              padding: "12px",
              borderRadius: "8px",
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "36px",
              paddingRight: "36px",
            }}
          >
            <Info
              size={16}
              className="text-muted mt-1"
              style={{ flexShrink: 0 }}
            />
            <div className="text-muted" style={{ fontSize: "12px" }}>
              Note: Add up to ₹50,000 to your wallet. For higher amounts,
              contact{" "}
              <a
                {...testIdProps("new-billing-support-email")}
                href="mailto:support@zoop.one"
                className="text-decoration-underline text-dark"
              >
                support@zoop.one
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="d-flex gap-3">
          <StyledButton
            id="new-billing-add-balance-cancel"
            style={{
              flex: "1",
              backgroundColor: "transparent",
              color: "#6B7280",
              border: "0px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              textDecoration: "underline",
            }}
            hoverStyle={{
              flex: "1",
              backgroundColor: "transparent",
              color: "#6B7280",
              border: "0px",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              textDecoration: "underline",
            }}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </StyledButton>

          <StyledButton
            id="new-billing-add-balance-submit"
            style={{
              flex: "1",
              backgroundColor: "#000000",
              color: "#FFFFFF",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              border: "none",
            }}
            hoverStyle={{
              flex: "1",
              backgroundColor: "#000000",
              color: "#FFFFFF",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              border: "none",
            }}
            onClick={handleAddBalance}
            disabled={
              !selectedWallet || !amount || isLoading || amountError != ""
            }
            isLoading={isLoading}
          >
            Add Balance
          </StyledButton>
        </div>
      </div>
    </div>
    </PortalOverlay>
  );
};

export default AddBalanceDialog;
